/**
 * SKOP Simulation Panel
 *
 * Displays transaction simulation results with fees and risk analysis.
 */

import { useState, useEffect } from 'react';
import {
  simulateSwap,
  type SimulationResult,
  type SimulationRequest,
  getRiskColor,
  getRiskLabel,
  formatFee,
} from '../lib/simulation';

interface SimulationPanelProps {
  tokenAddress: string;
  action: 'buy' | 'sell';
  amount: number;
  slippageBps: number;
  userWallet: string;
  onSimulationComplete?: (result: SimulationResult) => void;
}

export function SimulationPanel({
  tokenAddress,
  action,
  amount,
  slippageBps,
  userWallet,
  onSimulationComplete,
}: SimulationPanelProps) {
  const [result, setResult] = useState<SimulationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (!tokenAddress || !amount || !userWallet) {
      setResult(null);
      return;
    }

    const runSimulation = async () => {
      setLoading(true);
      setError(null);

      try {
        const params: SimulationRequest = {
          tokenAddress,
          action,
          amount,
          slippageBps,
          userWallet,
        };

        const simResult = await simulateSwap(params);
        setResult(simResult);
        onSimulationComplete?.(simResult);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Simulation failed');
        setResult(null);
      } finally {
        setLoading(false);
      }
    };

    // Debounce simulation
    const timer = setTimeout(runSimulation, 500);
    return () => clearTimeout(timer);
  }, [tokenAddress, action, amount, slippageBps, userWallet, onSimulationComplete]);

  if (!tokenAddress || !amount || !userWallet) {
    return null;
  }

  return (
    <div style={{
      padding: '12px',
      background: 'var(--bg-tertiary)',
      borderRadius: 'var(--radius-md)',
      marginBottom: '12px',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: loading || result ? '12px' : 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2v-4M9 21H5a2 2 0 0 1-2-2v-4"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
          <span style={{ fontSize: '13px', fontWeight: 600 }}>Transaction Simulation</span>
          <span style={{
            padding: '2px 6px',
            fontSize: '10px',
            fontWeight: 600,
            background: 'rgba(0, 240, 255, 0.1)',
            color: 'var(--accent)',
            borderRadius: 'var(--radius-sm)',
          }}>
            SIMULATION
          </span>
        </div>
        {result && (
          <button
            onClick={() => setShowDetails(!showDetails)}
            style={{
              padding: '4px 8px',
              fontSize: '11px',
              color: 'var(--text-secondary)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {showDetails ? 'Hide Details' : 'Show Details'}
          </button>
        )}
      </div>

      {loading && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: 'var(--text-secondary)',
          fontSize: '12px',
        }}>
          <div
            className="animate-spin"
            style={{
              width: '14px',
              height: '14px',
              border: '2px solid var(--border)',
              borderTopColor: 'var(--accent)',
              borderRadius: '50%',
            }}
          />
          Simulating transaction...
        </div>
      )}

      {error && (
        <div style={{
          padding: '8px 12px',
          background: 'rgba(255, 59, 59, 0.1)',
          borderRadius: 'var(--radius-sm)',
          color: 'var(--error)',
          fontSize: '12px',
        }}>
          {error}
        </div>
      )}

      {result && !loading && (
        <div>
          {/* Summary Row */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            background: result.success
              ? 'rgba(0, 255, 136, 0.05)'
              : 'rgba(255, 59, 59, 0.05)',
            borderRadius: 'var(--radius-sm)',
            marginBottom: showDetails ? '12px' : 0,
          }}>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                Expected Output
              </div>
              <div style={{ fontSize: '16px', fontWeight: 600 }}>
                {action === 'buy'
                  ? `${parseInt(result.expectedOutput).toLocaleString()} tokens`
                  : `${parseFloat(result.expectedOutput).toFixed(4)} SOL`
                }
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                Price Impact
              </div>
              <div style={{
                fontSize: '14px',
                fontWeight: 600,
                color: result.priceImpact > 5 ? 'var(--error)' :
                  result.priceImpact > 2 ? 'var(--warning)' : 'var(--text-primary)',
              }}>
                {result.priceImpact.toFixed(2)}%
              </div>
            </div>
          </div>

          {/* Risk Indicators */}
          {(result.risks.slippageRisk !== 'low' ||
            result.risks.mevExposure !== 'low' ||
            result.risks.sandwichRisk !== 'low' ||
            result.risks.liquidityRisk !== 'low') && (
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px',
              marginBottom: showDetails ? '12px' : 0,
            }}>
              {Object.entries(result.risks).map(([key, level]) => {
                if (level === 'low') return null;
                const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
                return (
                  <span
                    key={key}
                    style={{
                      padding: '4px 8px',
                      fontSize: '11px',
                      fontWeight: 500,
                      background: `${getRiskColor(level)}15`,
                      color: getRiskColor(level),
                      borderRadius: 'var(--radius-sm)',
                    }}
                  >
                    {label}: {getRiskLabel(level)}
                  </span>
                );
              })}
            </div>
          )}

          {/* Warnings */}
          {result.warnings.length > 0 && (
            <div style={{ marginBottom: showDetails ? '12px' : 0 }}>
              {result.warnings.map((warning, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px 10px',
                    fontSize: '11px',
                    color: '#ffb800',
                    background: 'rgba(255, 184, 0, 0.1)',
                    borderLeft: '3px solid #ffb800',
                    borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
                    marginBottom: i < result.warnings.length - 1 ? '4px' : 0,
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '4px', flexShrink: 0 }}>
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  {warning}
                </div>
              ))}
            </div>
          )}

          {/* Detailed Breakdown */}
          {showDetails && (
            <div style={{
              borderTop: '1px solid var(--border)',
              paddingTop: '12px',
            }}>
              {/* Fees Breakdown */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{
                  fontSize: '11px',
                  color: 'var(--text-tertiary)',
                  marginBottom: '8px',
                  fontWeight: 600,
                }}>
                  FEE BREAKDOWN
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '8px',
                }}>
                  <FeeRow label="Network Fee" value={formatFee(result.fees.networkFee)} />
                  <FeeRow label="Priority Fee" value={formatFee(result.fees.priorityFee)} />
                  <FeeRow label="SKōP Fee" value={formatFee(result.fees.skopFee)} />
                  <FeeRow label="Platform Fee" value={formatFee(result.fees.platformFee)} />
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '8px',
                  paddingTop: '8px',
                  borderTop: '1px solid var(--border)',
                  fontSize: '13px',
                  fontWeight: 600,
                }}>
                  <span>Total Fees</span>
                  <span style={{ color: 'var(--accent)' }}>
                    {formatFee(result.fees.totalFees)}
                  </span>
                </div>
              </div>

              {/* Output Details */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '8px',
              }}>
                <div style={{
                  padding: '8px',
                  background: 'var(--surface-secondary)',
                  borderRadius: 'var(--radius-sm)',
                }}>
                  <div style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>
                    Min Output (w/ slippage)
                  </div>
                  <div style={{ fontSize: '12px', fontWeight: 500 }}>
                    {action === 'buy'
                      ? `${parseInt(result.minOutput).toLocaleString()} tokens`
                      : `${parseFloat(result.minOutput).toFixed(4)} SOL`
                    }
                  </div>
                </div>
                {result.computeUnits && (
                  <div style={{
                    padding: '8px',
                    background: 'var(--surface-secondary)',
                    borderRadius: 'var(--radius-sm)',
                  }}>
                    <div style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>
                      Compute Units
                    </div>
                    <div style={{ fontSize: '12px', fontWeight: 500 }}>
                      {result.computeUnits.toLocaleString()} CU
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FeeRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '12px',
    }}>
      <span style={{ color: 'var(--text-tertiary)' }}>{label}</span>
      <span>{value}</span>
    </div>
  );
}
